import { Patient } from '../patient/patient';
import { IRepository } from './repository';

/**
 * Simple in-memory {@link Patient} repository
 */

export class PatientRepository implements IRepository<Patient> {
    /**
     * Method to get all the {@link Patient Patients} stored within this {@link PatientRepository}.
     * @returns An {@link Array} of all the {@link Patient Patients} stored within this {@link PatientRepository}.
     */
    getAll(): Patient[] {
        return Array.from(this.patients.values());
    }

    add(p: Patient) {
        this.patients.set(p.id, p);
    }

    /**
     * Method to remove {@link Patient} using it's unique runtime {@link Patient.id}.
     * @param id The unique runtime {@link Patient.id} of the {@link Patient}.
     * @returns true if {@link Patient} with the given {@link id} exists and has been removed,\
     * or false if {@link Patient} with that id does not exist.
     */
    delete(id: string): boolean {
        return this.patients.delete(id);
    }

    /**
     * Method to get {@link Patient} by the DICOM PatientID.\
     * To get {@link Patient} by it's unique run-time identifier, see {@link getById}.\
     * Note: If possible, always prefer {@link getById}-method over this one as it is more efficient.
     * @param patientId DICOM PatientID attribute
     * @returns A {@link Patient} with the given {@link patientId}. Null if no such {@link Patient} exists.
     */
    getByPatientId(patientId: string): Patient | null {
        const patient = Array.from(this.patients.values()).find(
            (x) => x.dataSet.PatientID === patientId
        );
        return patient ?? null;
    }

    /**
     * Method to get {@link Patient} by it's unique run-time {@link Patient.id}.\
     * This is not the PatientID stored in DICOM data. To get Patient by it's PatientID, use {@link getByPatientId}.
     * @param id {@link Patient}'s runtime {@link Patient.id id}.
     * @returns A {@link Patient} with the given {@link id} or null if no {@link Patient} with that {@link id} exists.
     */
    getById(id: string): Patient | null {
        const patient = this.patients.get(id);
        return patient ?? null;
    }

    /**
     * {@link Map} of all the {@link Patient Patients} stored within this {@link PatientRepository}.\
     * Key is the {@link Patient Patient's} unique identifier {@link Patient.id}.\
     * Value is the {@link Patient}.
     */
    private patients = new Map<string, Patient>();
}
